/**
 * Reglas de la pantalla de un rol.
 *
 * Es la pantalla donde un error se paga más caro de toda la consola —tildar mal
 * un permiso es dar acceso que nadie pidió—, así que lo que se puede sacar de
 * la vista y testear, se saca.
 */

/** El estado de un rol tal como lo edita la pantalla. */
export interface EstadoDelRol {
  descripcion: string;
  permisos: ReadonlySet<string>;
}

/**
 * Si el formulario difiere de lo que vino del servidor.
 *
 * Gobierna que «Guardar» aparezca solo cuando hay algo que guardar, como en
 * `RoleDetailPage` de `imprenta`. Sin esto el botón está siempre disponible y
 * deja de decir nada.
 *
 * **Se comparan los conjuntos, no sus tamaños.** Tildar uno y destildar otro
 * deja la misma cantidad de permisos y es exactamente el cambio que más
 * importa no perder.
 */
export function hayCambios(original: EstadoDelRol, actual: EstadoDelRol): boolean {
  if (original.descripcion !== actual.descripcion) return true;
  if (original.permisos.size !== actual.permisos.size) return true;
  for (const codigo of actual.permisos) {
    if (!original.permisos.has(codigo)) return true;
  }
  return false;
}

/**
 * Busca un rol por nombre, **sin distinguir mayúsculas**.
 *
 * Es la misma regla que ya aplican los endpoints: los roles dinámicos se
 * guardan siempre en minúscula —los normaliza el plugin al crearlos— y
 * `roles.ts` del worker los direcciona con cualquier capitalización.
 *
 * Sin esto, crear «Prueba Manual» y redirigir a `/roles/Prueba%20Manual`
 * mostraba «no existe un rol con ese nombre» al recargar, sobre un rol que
 * acababa de crearse. Los base sí conservan su capitalización, porque vienen
 * de código.
 */
export function buscarRol<T extends { nombre: string }>(
  roles: readonly T[],
  nombre: string,
): T | undefined {
  const buscado = nombre.trim().toLowerCase();
  return roles.find((r) => r.nombre.trim().toLowerCase() === buscado);
}
